/**
 * Document caching utility for citation viewer
 *
 * Caches downloaded documents and parsed text to improve performance.
 * Features:
 * - TTL (Time To Live) expiration for stale entries
 * - Max size limit with LRU (Least Recently Used) eviction
 * - Memory-safe storage using Uint8Array (prevents ArrayBuffer detachment)
 * - Automatic background cleanup of expired entries
 */

import { logger } from "@/telemetry";

interface CachedDocument {
  /** Raw document data as Uint8Array (doesn't get detached like ArrayBuffer) */
  data: Uint8Array;

  /** Parsed text content from the document (optional) */
  parsedText?: string;

  /** Timestamp when the document was cached */
  timestamp: number;

  /** Timestamp when the document was last accessed (for LRU) */
  lastAccessed: number;

  /** MIME type of the document */
  mimeType?: string;
}

/**
 * In-memory document cache with TTL and LRU eviction
 *
 * @example
 * ```typescript
 * // Store a document
 * documentCache.set('doc-123', arrayBuffer, parsedText, 'application/pdf');
 *
 * // Retrieve a document
 * const cached = documentCache.get('doc-123');
 * if (cached) {
 *   // Use cached.data (Uint8Array)
 * }
 *
 * // Check cache stats
 * const stats = documentCache.getStats();
 * console.log(`Cache: ${stats.size} docs, ${stats.totalBytes / 1024 / 1024}MB`);
 * ```
 */
class DocumentCache {
  private cache = new Map<string, CachedDocument>();

  /** Time-to-live: 15 minutes */
  private readonly TTL = 15 * 60 * 1000;

  /**
   * Maximum cache size: 150MB
   *
   * Increased from 50MB to accommodate sessions with multiple high-resolution PDFs.
   * High-resolution PDFs can easily be 5-10MB each, and a conservative 50MB limit
   * would cause frequent cache cycling with just 5-10 open documents.
   * 150MB provides a better balance for desktop browsers while still preventing
   * excessive memory usage.
   */
  private readonly MAX_SIZE = 150 * 1024 * 1024;

  /** Current total size of cached documents in bytes */
  private currentSize = 0;

  /**
   * Store a document in the cache
   * @param documentId - Unique document identifier
   * @param arrayBuffer - Raw document data
   * @param parsedText - Optional parsed text content
   * @param mimeType - Optional MIME type
   */
  set(documentId: string, arrayBuffer: ArrayBuffer, parsedText?: string, mimeType?: string): void {
    const documentSize = arrayBuffer.byteLength;

    // Check if document already exists (update case)
    const existing = this.cache.get(documentId);

    if (existing) {
      this.currentSize -= existing.data.byteLength;
      this.cache.delete(documentId);
    }

    // Evict LRU entries if adding this document would exceed max size
    this.evictIfNeeded(documentSize);

    // Create a completely independent copy to prevent detachment issues
    // We create a new ArrayBuffer and copy the bytes, so the original can be detached
    const newBuffer = new ArrayBuffer(documentSize);
    const newView = new Uint8Array(newBuffer);
    const sourceView = new Uint8Array(arrayBuffer);

    newView.set(sourceView);

    // Store the independent Uint8Array
    const data = new Uint8Array(newBuffer);
    const now = Date.now();

    this.cache.set(documentId, {
      data,
      parsedText,
      mimeType,
      timestamp: now,
      lastAccessed: now,
    });

    this.currentSize += documentSize;

    logger.info(
      `📦 Document cached: ${documentId} (${(documentSize / 1024).toFixed(1)}KB, ` +
        `total: ${(this.currentSize / 1024 / 1024).toFixed(1)}MB)`,
    );
  }

  /**
   * Evict least recently used documents until there's room for the new document
   * @param requiredSpace - Space needed for the new document in bytes
   */
  private evictIfNeeded(requiredSpace: number): void {
    // If the document itself is larger than max size, we'll still cache it
    // but evict everything else first
    const targetSize = Math.max(0, this.MAX_SIZE - requiredSpace);

    if (this.currentSize <= targetSize) {
      return; // No eviction needed
    }

    // Sort entries by lastAccessed (oldest first)
    const entries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.lastAccessed - b.lastAccessed,
    );

    let evictedCount = 0;

    for (const [id, cached] of entries) {
      if (this.currentSize <= targetSize) {
        break;
      }

      this.currentSize -= cached.data.byteLength;
      this.cache.delete(id);
      evictedCount++;
    }

    if (evictedCount > 0) {
      logger.info(
        `📦 LRU eviction: removed ${evictedCount} document(s) to free space ` +
          `(now: ${(this.currentSize / 1024 / 1024).toFixed(1)}MB)`,
      );
    }
  }

  /**
   * Retrieve a document from the cache
   * @param documentId - Unique document identifier
   * @returns Cached document or null if not found or expired
   */
  get(documentId: string): CachedDocument | null {
    const cached = this.cache.get(documentId);

    if (!cached) {
      logger.info(`📦 Cache miss: ${documentId}`);

      return null;
    }

    // Check if cache entry has expired
    const now = Date.now();
    const age = now - cached.timestamp;

    if (age > this.TTL) {
      logger.info(`📦 Cache expired: ${documentId} (age: ${(age / 1000).toFixed(0)}s)`);
      this.currentSize -= cached.data.byteLength;
      this.cache.delete(documentId);

      return null;
    }

    // Update last accessed time for LRU tracking
    cached.lastAccessed = now;

    logger.info(`📦 Cache hit: ${documentId} (age: ${(age / 1000).toFixed(0)}s)`);

    return cached;
  }

  /**
   * Update parsed text for a cached document
   * @param documentId - Unique document identifier
   * @param parsedText - Parsed text content
   * @returns true if document was found and updated, false otherwise
   */
  updateParsedText(documentId: string, parsedText: string): boolean {
    const cached = this.cache.get(documentId);

    if (!cached) {
      return false;
    }

    cached.parsedText = parsedText;
    logger.info(`📦 Updated parsed text for: ${documentId}`);

    return true;
  }

  /**
   * Check if a document is cached and not expired
   * @param documentId - Unique document identifier
   * @returns true if document is cached and valid
   */
  has(documentId: string): boolean {
    return this.get(documentId) !== null;
  }

  /**
   * Remove a specific document from the cache
   * @param documentId - Unique document identifier
   */
  delete(documentId: string): void {
    const cached = this.cache.get(documentId);

    if (cached) {
      this.currentSize -= cached.data.byteLength;
      this.cache.delete(documentId);
      logger.info(`📦 Document removed from cache: ${documentId}`);
    }
  }

  /**
   * Clear all cached documents
   */
  clear(): void {
    const count = this.cache.size;

    this.cache.clear();
    this.currentSize = 0;
    logger.info(`📦 Cache cleared (${count} documents removed)`);
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    totalBytes: number;
    maxBytes: number;
    usagePercent: number;
    documents: Array<{
      id: string;
      size: number;
      age: number;
      lastAccessed: number;
      hasParsedText: boolean;
    }>;
  } {
    const now = Date.now();
    const documents: Array<{
      id: string;
      size: number;
      age: number;
      lastAccessed: number;
      hasParsedText: boolean;
    }> = [];

    this.cache.forEach((cached, id) => {
      documents.push({
        id,
        size: cached.data.byteLength,
        age: now - cached.timestamp,
        lastAccessed: now - cached.lastAccessed,
        hasParsedText: !!cached.parsedText,
      });
    });

    return {
      size: this.cache.size,
      totalBytes: this.currentSize,
      maxBytes: this.MAX_SIZE,
      usagePercent: (this.currentSize / this.MAX_SIZE) * 100,
      documents,
    };
  }

  /**
   * Remove expired documents from the cache
   * @returns Number of documents removed
   */
  cleanup(): number {
    const now = Date.now();
    let removed = 0;
    let freedBytes = 0;

    this.cache.forEach((cached, id) => {
      if (now - cached.timestamp > this.TTL) {
        freedBytes += cached.data.byteLength;
        this.cache.delete(id);
        removed++;
      }
    });

    this.currentSize -= freedBytes;

    if (removed > 0) {
      logger.info(
        `📦 Cache cleanup: ${removed} expired documents removed ` +
          `(freed: ${(freedBytes / 1024).toFixed(1)}KB)`,
      );
    }

    return removed;
  }

  /**
   * Get the maximum cache size in bytes
   */
  getMaxSize(): number {
    return this.MAX_SIZE;
  }

  /**
   * Get current cache size in bytes
   */
  getCurrentSize(): number {
    return this.currentSize;
  }
}

// Singleton instance
export const documentCache = new DocumentCache();

// Run cleanup every 5 minutes
if (typeof window !== "undefined") {
  setInterval(
    () => {
      documentCache.cleanup();
    },
    5 * 60 * 1000,
  );
}
